import { motion } from "motion/react"

type IllustrationProps = {
    accent: string
}

export function NotFoundIllustration({ accent }: IllustrationProps) {
    return (
        <div className="relative mx-auto size-[220px] sm:size-[260px]" aria-hidden>
            <motion.div
                className="absolute inset-0 rounded-[28px] border border-white/10 bg-white/[0.04]"
                animate={{ rotate: [-1.5, 1.5, -1.5] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                style={{ boxShadow: `0 0 40px ${accent}22` }}
            />
            <div className="absolute inset-3 overflow-hidden rounded-[20px] border border-white/15 bg-[#0d1830]">
                <div
                    className="absolute inset-0 opacity-30"
                    style={{
                        backgroundImage:
                            "repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(255,255,255,0.04) 3px, rgba(255,255,255,0.04) 4px)",
                    }}
                />
                <motion.div
                    className="absolute inset-0 flex items-center justify-center"
                    animate={{ opacity: [0.35, 0.7, 0.35] }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}>
                    <span
                        className="select-none text-[72px] font-black leading-none sm:text-[88px]"
                        style={{ fontFamily: "'Tilda Sans', sans-serif", color: `${accent}88` }}>
                        ?
                    </span>
                </motion.div>
                <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-black/70 to-transparent" />
            </div>
            <motion.div
                className="absolute -right-3 top-8 rounded-xl border border-dashed border-white/25 bg-white/[0.06] px-3 py-2"
                animate={{ y: [0, -6, 0], rotate: [8, 12, 8] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}>
                <div className="text-[10px] font-bold uppercase tracking-widest text-white/40" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    билет
                </div>
                <div className="mt-1 text-[11px] text-white/25 line-through" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    CK-404-VOID
                </div>
            </motion.div>
            <div
                className="absolute -bottom-2 left-1/2 h-2 w-[70%] -translate-x-1/2 rounded-full blur-md"
                style={{ background: `${accent}55` }}
            />
        </div>
    )
}

export function ForbiddenIllustration({ accent }: IllustrationProps) {
    return (
        <div className="relative mx-auto size-[220px] sm:size-[260px]" aria-hidden>
            <div
                className="absolute inset-x-6 bottom-6 top-16 rounded-t-[40px] border border-white/10 bg-gradient-to-b from-white/[0.07] to-white/[0.02]"
                style={{ boxShadow: `inset 0 0 30px ${accent}15` }}
            />
            {[0, 1].map(i => (
                <motion.div
                    key={i}
                    className="absolute bottom-[88px] size-3 rounded-full border-2 border-white/30 bg-[#1a2d55]"
                    style={{ left: i === 0 ? "28%" : "68%" }}
                    animate={{ scale: [1, 1.08, 1] }}
                    transition={{ duration: 2, delay: i * 0.3, repeat: Infinity }}
                />
            ))}
            <motion.div
                className="absolute bottom-[72px] left-[22%] right-[22%] h-1 rounded-full"
                style={{ background: accent, boxShadow: `0 0 12px ${accent}88` }}
                animate={{ opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 1.8, repeat: Infinity }}
            />
            <motion.div
                className="absolute left-1/2 top-6 -translate-x-1/2 text-[64px] leading-none sm:text-[76px]"
                animate={{ rotate: [-6, 6, -6] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}>
                🚫
            </motion.div>
            <div
                className="absolute bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white/50"
                style={{ fontFamily: "'JetBrains Mono', monospace", borderColor: `${accent}44`, background: `${accent}12` }}>
                staff only
            </div>
        </div>
    )
}

export function ServerErrorIllustration({ accent }: IllustrationProps) {
    return (
        <div className="relative mx-auto size-[220px] sm:size-[260px]" aria-hidden>
            <motion.div
                className="absolute left-1/2 top-2 size-16 -translate-x-1/2 rounded-2xl border border-white/15 bg-white/[0.06]"
                animate={{ y: [0, -4, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}>
                <div className="flex h-full items-center justify-center text-[32px]">🎥</div>
            </motion.div>
            <motion.div
                className="error-projector-beam absolute left-1/2 top-[72px] h-[120px] w-[140px] -translate-x-1/2 origin-top"
                style={{
                    background: `conic-gradient(from 180deg at 50% 0%, transparent 38%, ${accent}44 50%, transparent 62%)`,
                }}
            />
            <div className="absolute inset-x-8 bottom-8 top-[100px] overflow-hidden rounded-[18px] border border-white/12 bg-[#0a1228]">
                <motion.div
                    className="error-static-noise absolute inset-0"
                    animate={{ opacity: [0.15, 0.45, 0.2, 0.5, 0.15] }}
                    transition={{ duration: 0.8, repeat: Infinity }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                    <span
                        className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/35"
                        style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        no signal
                    </span>
                </div>
            </div>
            <motion.div
                className="absolute -right-1 bottom-12 text-[40px]"
                animate={{ rotate: [0, 12, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity }}>
                🍿
            </motion.div>
        </div>
    )
}
